// Administration : utilisateurs, invitations, webhooks n8n, clés API
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Send, Copy, CheckCircle2, XCircle, FlaskConical, Mail, Download, Eye, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { OngletJournalWebhooks } from "@/components/OngletJournalWebhooks";
import { OngletLienPublic } from "@/components/OngletLienPublic";
import { ROLES, STATUTS_COMPTE, EVENEMENTS_WEBHOOK, POLES } from "@/lib/constants";
import { formatJour } from "@/lib/dates";

// ——— Onglet Utilisateurs ———
const OngletUtilisateurs = () => {
  const { user: moi } = useAuth();
  const [users, setUsers] = useState([]);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const modifier = async (id, update, message) => {
    try {
      await api.patch(`/users/${id}`, update);
      toast.success(message);
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enAttente = users.filter((u) => u.statut === "en_attente");
  const autres = users.filter((u) => u.statut !== "en_attente");

  return (
    <div className="space-y-6">
      {enAttente.length > 0 && (
        <div className="bg-accent/10 border border-accent/40 rounded-2xl p-5" data-testid="pending-accounts-section">
          <h3 className="font-serif text-xl font-semibold mb-3">Comptes en attente d'approbation ({enAttente.length})</h3>
          <div className="space-y-2">
            {enAttente.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 bg-card rounded-xl px-4 py-3" data-testid={`pending-user-${u.email}`}>
                <UserAvatar user={u} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
                <Select value={u.role} onValueChange={(v) => modifier(u.id, { role: v }, "Rôle attribué")}>
                  <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid={`pending-role-select-${u.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => modifier(u.id, { statut: "actif" }, "Compte approuvé")} className="rounded-full gap-1.5 bg-success hover:bg-success/90" data-testid={`approve-user-${u.email}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approuver
                </Button>
                <Button size="sm" variant="ghost" onClick={() => modifier(u.id, { statut: "desactive" }, "Compte refusé")} className="rounded-full gap-1.5 text-destructive" data-testid={`refuse-user-${u.email}`}>
                  <XCircle className="h-3.5 w-3.5" /> Refuser
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="users-list">
        {autres.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 bg-card rounded-2xl border border-border px-5 py-3.5 carte-douce" data-testid={`user-row-${u.email}`}>
            <UserAvatar user={u} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{u.name} {u.id === moi.id && <span className="text-xs text-muted-foreground">(vous)</span>}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
            </div>
            <Badge variant={u.statut === "actif" ? "outline" : "secondary"} className={u.statut === "desactive" ? "bg-destructive/15 text-destructive" : ""}>
              {STATUTS_COMPTE[u.statut]}
            </Badge>
            <Select value={u.role} onValueChange={(v) => modifier(u.id, { role: v }, "Rôle modifié")} disabled={u.id === moi.id}>
              <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={u.pole_responsable || "__none__"}
              onValueChange={(v) => modifier(u.id, { pole_responsable: v === "__none__" ? "" : v }, "Pôle de responsabilité modifié")}
            >
              <SelectTrigger className="rounded-full h-8 w-40 text-xs" data-testid={`user-pole-select-${u.email}`}>
                <SelectValue placeholder="Aucun pôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Aucun pôle —</SelectItem>
                {Object.entries(POLES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            {u.id !== moi.id && (
              u.statut === "actif" ? (
                <Button size="sm" variant="ghost" onClick={() => modifier(u.id, { statut: "desactive" }, "Compte désactivé")} className="rounded-full text-destructive text-xs" data-testid={`deactivate-user-${u.email}`}>
                  Désactiver
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => modifier(u.id, { statut: "actif" }, "Compte réactivé")} className="rounded-full text-success text-xs" data-testid={`reactivate-user-${u.email}`}>
                  Réactiver
                </Button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ——— Onglet Invitations ———
const OngletInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("membre");

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/invitations");
      setInvitations(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const inviter = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invitations", { email, role });
      toast.success("Invitation créée — l'email sera envoyé via n8n (webhook user.invited)");
      setEmail("");
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const supprimer = async (id) => {
    try {
      await api.delete(`/invitations/${id}`);
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={inviter} className="flex flex-wrap items-end gap-3 bg-card rounded-2xl border border-border p-5 carte-douce">
        <div className="flex-1 min-w-[200px]">
          <Label>Email à inviter</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="personne@exemple.com" className="rounded-xl mt-1.5" data-testid="invitation-email-input" />
        </div>
        <div>
          <Label>Rôle</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-xl mt-1.5 w-36" data-testid="invitation-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="rounded-full gap-1.5" data-testid="invitation-submit-button">
          <Send className="h-4 w-4" /> Inviter
        </Button>
      </form>
      <div className="space-y-2">
        {invitations.map((i) => (
          <div key={i.id} className="flex items-center gap-3 bg-card rounded-2xl border border-border px-5 py-3 carte-douce" data-testid={`invitation-row-${i.email}`}>
            <div className="flex-1">
              <p className="text-sm font-medium">{i.email}</p>
              <p className="text-xs text-muted-foreground">Rôle proposé : {ROLES[i.role]} · créée le {formatJour(i.created_at)}</p>
            </div>
            <Badge variant={i.statut === "en_attente" ? "outline" : "secondary"}>{i.statut === "en_attente" ? "En attente" : "Acceptée"}</Badge>
            <button onClick={() => supprimer(i.id)} className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors" data-testid={`invitation-delete-${i.email}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {invitations.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucune invitation.</p>}
      </div>
    </div>
  );
};

// ——— Onglet Webhooks ———
const OngletWebhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [evenements, setEvenements] = useState([]);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/webhooks");
      setWebhooks(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const creer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/webhooks", { url, secret: secret || null, evenements });
      toast.success("Webhook configuré");
      setUrl(""); setSecret(""); setEvenements([]);
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const tester = async (id) => {
    try {
      await api.post(`/webhooks/${id}/test`);
      toast.success("Payload de test envoyé");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={creer} className="bg-card rounded-2xl border border-border p-5 space-y-4 carte-douce">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>URL du webhook (n8n)</Label>
            <Input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://n8n.inyange.com/webhook/…" className="rounded-xl mt-1.5" data-testid="webhook-url-input" />
          </div>
          <div>
            <Label>Secret (en-tête X-Inyange-Secret, optionnel)</Label>
            <Input value={secret} onChange={(e) => setSecret(e.target.value)} className="rounded-xl mt-1.5" data-testid="webhook-secret-input" />
          </div>
        </div>
        <div>
          <Label>Événements activés</Label>
          <div className="grid sm:grid-cols-3 gap-2 mt-1.5">
            {EVENEMENTS_WEBHOOK.map((ev) => (
              <label key={ev} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs cursor-pointer hover:bg-muted/50 transition-colors">
                <Checkbox
                  checked={evenements.includes(ev)}
                  onCheckedChange={() => setEvenements(evenements.includes(ev) ? evenements.filter((x) => x !== ev) : [...evenements, ev])}
                  data-testid={`webhook-event-${ev}`}
                />
                <span className="font-mono">{ev}</span>
              </label>
            ))}
          </div>
        </div>
        <Button type="submit" className="rounded-full" data-testid="webhook-create-button">Ajouter le webhook</Button>
      </form>
      <div className="space-y-2">
        {webhooks.map((w) => (
          <div key={w.id} className="flex flex-wrap items-center gap-3 bg-card rounded-2xl border border-border px-5 py-3.5 carte-douce" data-testid={`webhook-row-${w.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium font-mono truncate">{w.url}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {w.evenements.map((ev) => <Badge key={ev} variant="outline" className="text-[10px] font-mono">{ev}</Badge>)}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => tester(w.id)} className="rounded-full gap-1.5 text-xs" data-testid={`webhook-test-${w.id}`}>
              <FlaskConical className="h-3.5 w-3.5" /> Tester
            </Button>
            <Switch
              checked={w.actif}
              onCheckedChange={async (v) => { await api.patch(`/webhooks/${w.id}`, { actif: v }); charger(); }}
              data-testid={`webhook-toggle-${w.id}`}
            />
            <button
              onClick={async () => { await api.delete(`/webhooks/${w.id}`); charger(); }}
              className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
              data-testid={`webhook-delete-${w.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {webhooks.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucun webhook configuré.</p>}
      </div>
    </div>
  );
};

// ——— Onglet Clés API ———
const OngletClesApi = () => {
  const [cles, setCles] = useState([]);
  const [nom, setNom] = useState("");
  const [cleGeneree, setCleGeneree] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/api-keys");
      setCles(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const generer = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api-keys", { nom });
      setCleGeneree(data.cle);
      setNom("");
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={generer} className="flex flex-wrap items-end gap-3 bg-card rounded-2xl border border-border p-5 carte-douce">
        <div className="flex-1 min-w-[200px]">
          <Label>Nom de la clé</Label>
          <Input required value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex. n8n — ventes Ticketmaster" className="rounded-xl mt-1.5" data-testid="apikey-nom-input" />
        </div>
        <Button type="submit" className="rounded-full" data-testid="apikey-create-button">Générer une clé</Button>
      </form>

      {cleGeneree && (
        <div className="rounded-2xl border border-accent bg-accent/10 p-5 space-y-2" data-testid="apikey-generated">
          <p className="text-sm font-semibold">Clé générée — copiez-la maintenant, elle ne sera plus affichée :</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-card rounded-xl px-3 py-2 break-all border border-border" data-testid="apikey-generated-value">{cleGeneree}</code>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => { navigator.clipboard.writeText(cleGeneree); toast.success("Clé copiée"); }} data-testid="apikey-copy-button">
              <Copy className="h-3.5 w-3.5" /> Copier
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Usage : <code className="font-mono">POST /api/ingest/ticket-sales</code> avec l'en-tête <code className="font-mono">X-API-Key</code> —
            payload <code className="font-mono">{'{"night": "friday"|"saturday", "sold": 350, "date": "2026-07-01T18:00"}'}</code>
          </p>
        </div>
      )}

      <div className="space-y-2">
        {cles.map((c) => (
          <div key={c.id} className="flex items-center gap-3 bg-card rounded-2xl border border-border px-5 py-3.5 carte-douce" data-testid={`apikey-row-${c.id}`}>
            <div className="flex-1">
              <p className="text-sm font-medium">{c.nom}</p>
              <p className="text-xs text-muted-foreground font-mono">{c.prefixe}… · créée le {formatJour(c.created_at)}</p>
            </div>
            <Switch
              checked={c.actif}
              onCheckedChange={async (v) => { await api.patch(`/api-keys/${c.id}`, { actif: v }); charger(); }}
              data-testid={`apikey-toggle-${c.id}`}
            />
            <button
              onClick={async () => { if (window.confirm("Supprimer cette clé API ?")) { await api.delete(`/api-keys/${c.id}`); charger(); } }}
              className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
              data-testid={`apikey-delete-${c.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {cles.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucune clé API.</p>}
      </div>
    </div>
  );
};

// ——— Onglet Modèles email ———
const OngletModelesEmail = () => {
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/email-templates");
        setTemplates(data);
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, []);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const telecharger = (name) => {
    // Ouvre l'URL téléchargeable — le cookie httpOnly session_token est envoyé (same-origin)
    window.location.href = `${backendUrl}/api/email-templates/${name}/download`;
  };

  const previsualiser = (name) => {
    window.open(`${backendUrl}/api/email-templates/${name}/preview`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6" data-testid="email-templates-section">
      <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-sm text-foreground">
        <p className="font-serif text-lg font-semibold mb-2">Comment les utiliser dans Brevo</p>
        <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
          <li><strong>Aperçu</strong> : ouvre le rendu dans un onglet avec un payload d'exemple — vérifiez Gmail / Outlook web / mobile.</li>
          <li><strong>Télécharger</strong> le HTML puis dans Brevo → <em>Campagnes → Modèles email → Nouveau modèle → HTML personnalisé</em> et coller le contenu.</li>
          <li>Le mapping n8n → Brevo (variables <code className="font-mono text-xs">{"{{ params.xxx }}"}</code>) est détaillé dans <code className="font-mono text-xs">/app/email_templates/README.md</code>.</li>
        </ol>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {templates.map((t) => (
          <div key={t.name} className="bg-card rounded-2xl border border-border p-6 space-y-4 carte-douce" data-testid={`email-template-${t.name}`}>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 p-2.5 text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl font-semibold">{t.titre}</h3>
                <Badge variant="outline" className="font-mono text-[10px] mt-1">{t.evenement}</Badge>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.description}</p>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Variables Brevo</p>
              <div className="flex flex-wrap gap-1.5">
                {t.params.map((p) => (
                  <code key={p} className="font-mono text-[11px] bg-muted rounded-md px-2 py-0.5">{`{{ params.${p} }}`}</code>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => previsualiser(t.name)}
                variant="outline"
                className="flex-1 rounded-full gap-1.5"
                data-testid={`email-template-preview-${t.name}`}
              >
                <Eye className="h-4 w-4" /> Aperçu
              </Button>
              <Button
                onClick={() => telecharger(t.name)}
                className="flex-1 rounded-full gap-1.5"
                data-testid={`email-template-download-${t.name}`}
              >
                <Download className="h-4 w-4" /> Télécharger HTML
              </Button>
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-sm text-muted-foreground text-center py-8 md:col-span-2">Aucun modèle disponible.</p>}
      </div>

      {/* Prompt n8n à donner à Claude */}
      <div className="rounded-2xl border border-border bg-card p-6 space-y-3 carte-douce" data-testid="n8n-setup-prompt-card">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-accent/20 p-2.5 text-accent-foreground">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-xl font-semibold">Configuration n8n — prompt pour Claude</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Prompt complet à copier-coller dans Claude pour qu'il configure votre instance n8n
              (routage des 6 événements, mapping Brevo, sécurité, et 10 quality-of-life suggestions).
            </p>
          </div>
        </div>
        <div>
          <Button
            onClick={() => { window.location.href = `${backendUrl}/api/email-templates/n8n-setup-prompt/download`; }}
            variant="outline"
            className="rounded-full gap-1.5"
            data-testid="n8n-setup-prompt-download"
          >
            <Download className="h-4 w-4" /> Télécharger le prompt (.md)
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();
  if (user.role !== "admin") {
    return <p className="text-center py-24 text-muted-foreground">Cette page est réservée aux administrateurs.</p>;
  }
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-serif text-4xl font-bold">Administration</h1>
        <p className="text-muted-foreground text-sm mt-1">Utilisateurs, invitations, webhooks n8n, journal des livraisons et clés API</p>
      </div>
      <Tabs defaultValue="utilisateurs">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="utilisateurs" className="rounded-full" data-testid="admin-tab-utilisateurs">Utilisateurs</TabsTrigger>
          <TabsTrigger value="invitations" className="rounded-full" data-testid="admin-tab-invitations">Invitations</TabsTrigger>
          <TabsTrigger value="webhooks" className="rounded-full" data-testid="admin-tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="modeles-email" className="rounded-full" data-testid="admin-tab-modeles-email">Modèles email</TabsTrigger>
          <TabsTrigger value="journal" className="rounded-full" data-testid="admin-tab-journal">Journal webhooks</TabsTrigger>
          <TabsTrigger value="lien-public" className="rounded-full" data-testid="admin-tab-lien-public">Lien public</TabsTrigger>
          <TabsTrigger value="cles" className="rounded-full" data-testid="admin-tab-cles">Clés API</TabsTrigger>
        </TabsList>
        <TabsContent value="utilisateurs" className="mt-6"><OngletUtilisateurs /></TabsContent>
        <TabsContent value="invitations" className="mt-6"><OngletInvitations /></TabsContent>
        <TabsContent value="webhooks" className="mt-6"><OngletWebhooks /></TabsContent>
        <TabsContent value="modeles-email" className="mt-6"><OngletModelesEmail /></TabsContent>
        <TabsContent value="journal" className="mt-6"><OngletJournalWebhooks /></TabsContent>
        <TabsContent value="lien-public" className="mt-6"><OngletLienPublic /></TabsContent>
        <TabsContent value="cles" className="mt-6"><OngletClesApi /></TabsContent>
      </Tabs>
    </div>
  );
}
